import { LevelSchema } from './level';
import { UNKNOWN_MECHANIC_CONFIG, getMechanicConfig } from './mechanic';
import { $basicScene } from '@/stores/basicSceneStore';
import {
  $levelName,
  $levelNumber,
  $levelPackNumber,
  $levelPattern,
  $levelTheme,
  setCameraAngleToOrigin,
} from '@/stores/settingsStore';
import type z from 'zod';

export function loadLevel(level: z.infer<typeof LevelSchema>) {
  const scene = $basicScene.get();
  if (!scene) return;

  // Clear existing mechanics
  scene.clearSceneObjects();

  // Load new mechanics
  for (const mechanic of level.levelMechanics) {
    let config = getMechanicConfig(mechanic.name);
    if (!config) {
      console.warn(`Unknown mechanic ID: ${mechanic.name}`);
      config = UNKNOWN_MECHANIC_CONFIG;
    }

    const props = config.decode(mechanic);
    if (!props) {
      console.warn(`Failed to decode mechanic: ${mechanic.name}`);
      continue;
    }

    const sceneObject = new config.clazz({
      ...props,
      ...config.props,
      ...config.loadProps,
    });
    scene.addSelectableObject(sceneObject);
  }

  // Load level settings
  $levelName.set(level.levelName);
  $levelNumber.set(level.levelNumber);
  $levelPackNumber.set(level.levelPackNumber);
  $levelTheme.set(level.levelTheme);
  $levelPattern.set(level.levelPattern);

  setCameraAngleToOrigin(level.levelCamAngle);
}

export function saveLevel(): z.input<typeof LevelSchema> {
  const scene = $basicScene.get();
  if (!scene) {
    throw new Error('No scene available');
  }

  const levelMechanics = scene.sceneObjects.sceneObjects.map(obj =>
    obj.encode(),
  );

  return LevelSchema.encode({
    levelName: $levelName.get(),
    levelNumber: $levelNumber.get(),
    levelPackNumber: $levelPackNumber.get(),
    levelTheme: $levelTheme.get(),
    levelPattern: $levelPattern.get(),
    levelCamAngle: scene.getCameraAngleDegrees(),
    levelMechanics,
  });
}
